import type { DataGridColumnProps } from '../../DataGrid/DataGrid';
import type {
  DropdownChangeFn,
  DropdownFilterCallback,
} from '../../Dropdown/Dropdown';
import type { DropdownOption } from '../../Dropdown/DropdownOption/DropdownOption';

import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getObjectTitle } from '../../../lib/helpers';

import { useContentObject } from '../../../hooks/api';

import Dropdown from '../../Dropdown/Dropdown';
import Loader from '../../Loader/Loader';

interface GridFilterValueFieldProps {
  /**
   * Callback function that is called when the filter value changes.
   */
  onChange: (value: string) => void;
  /**
   * Current filter value.
   */
  value?: string;
  /**
   * The column definition for the field being filtered.
   */
  column?: DataGridColumnProps;
  /**
   * Test ID for the field.
   */
  testId?: string;
}

const GridFilterValueDatasource = ({
  column,
  onChange,
  value = '',
  testId,
}: GridFilterValueFieldProps) => {
  const { t } = useTranslation();
  const fetchDatasource = column?.filterDatasourceFetch;

  const [pickedOption, setPickedOption] = useState<DropdownOption>();
  const [defaultOptions, setDefaultOptions] = useState<DropdownOption[]>([]);

  const { contentTypeName, id } =
    RegExp(/(?<contentTypeName>[^/]+)\/(?<id>[^/]+)$/).exec(value)?.groups ||
    {};

  const { entity: object } = useContentObject(contentTypeName, id, {
    pause: pickedOption?.value === value,
  });

  const selectedOption = useMemo(() => {
    if (pickedOption?.value === value) return pickedOption;
    if (!object) return;

    return {
      value,
      label: getObjectTitle(object),
    };
  }, [object, pickedOption, value]);

  const currentOptions = useMemo(
    () =>
      selectedOption
        ? [
            selectedOption,
            ...defaultOptions.filter((option) => option.value !== value),
          ]
        : defaultOptions,
    [defaultOptions, selectedOption, value],
  );

  const handleOnChange: DropdownChangeFn = (e) => {
    setPickedOption(
      e.option || { value: e.target.value, label: e.target.value },
    );
    onChange?.(e.target.value);
  };

  const filterCallback = useCallback<DropdownFilterCallback>(
    async (query, options, setIsLoading, currentValue) => {
      setIsLoading(true);

      try {
        let resOptions: DropdownOption[] | undefined = options;

        if (query !== currentValue && fetchDatasource) {
          if (query === '_initOnEmpty') {
            if (!defaultOptions.length) {
              resOptions = await fetchDatasource();
              if (resOptions?.length) setDefaultOptions(resOptions);
            } else {
              resOptions = defaultOptions;
            }
          } else {
            resOptions = await fetchDatasource(query);
          }
        }

        return selectedOption
          ? [
              selectedOption,
              ...(resOptions || []).filter((option) => option.value !== value),
            ]
          : resOptions || [];
      } finally {
        setIsLoading(false);
      }
    },
    [fetchDatasource, defaultOptions, selectedOption, value],
  );

  return (
    <Dropdown
      onChange={handleOnChange}
      options={currentOptions}
      value={value}
      loadingIcon={
        <div className="h-10 overflow-hidden flex justify-center items-center">
          <Loader type="spinner-grid" size="tiny" />
        </div>
      }
      renderEmpty={() => (
        <div className="p-2 text-slate-400/80 text-sm text-center">
          {t('Global.NoData')}
        </div>
      )}
      emptyOptions={
        <div className="p-2 text-slate-400/80 text-sm text-center">
          {t('Global.NoData')}
        </div>
      }
      filterCallback={filterCallback}
      filterCallbackOnEmpty={true}
      debounceTime={500}
      variant="filter"
      placeholder={t('ObjectsOfType.FilterValuePlaceholder')}
      additionalContainerClasses="border border-slate-200 dark:border-slate-800 rounded-lg"
      additionalDropdownClasses={'z-21 min-w-[calc(var(--button-width)+50px)]'}
      anchor={{ to: 'bottom end', gap: 12, padding: 16, offset: 74 }}
      additionalInputClasses="px-2 h-3.5 w-34"
      testId={testId}
    />
  );
};

export default GridFilterValueDatasource;
